import * as React from 'react';
import { cn } from '@/lib/utils';
import { useFieldControl } from '@/components/ui/field';

/**
 * The pill the designs draw wherever a boolean is a *setting* rather than a form
 * field — "Available for ordering", not "I agree".
 *
 * A `<button role="switch">` rather than a styled checkbox: the control has no
 * indeterminate state, is never submitted with a form, and `aria-checked` is
 * what a screen reader announces as "on"/"off" instead of "checked". It goes
 * through `useFieldControl`, so a `<Field label=…>` names it at any depth the
 * same way it names an `<Input>`, and an explicit `aria-label` still wins for
 * the call sites that have no Field — the permission matrix names 60 pills from
 * their resource and action.
 *
 * The knob moves by `justify-start`/`justify-end` rather than a translate, so
 * Arabic gets the same component instead of a second code path.
 */
export interface SwitchProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'type' | 'value'> {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  ({ checked, onChange, className, disabled, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'flex h-5 w-10 shrink-0 items-center rounded-full p-0.5 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'justify-end bg-accent-solid' : 'justify-start bg-subtle',
        className,
      )}
      {...useFieldControl(props)}
    >
      <span
        aria-hidden
        className={cn('h-4 w-4 rounded-full', checked ? 'bg-accent-on' : 'bg-line-strong')}
      />
    </button>
  ),
);
Switch.displayName = 'Switch';

/**
 * One row of a settings card: a name, the sentence under it that says what the
 * setting actually does, and the pill.
 *
 * The sub-label is wired as the switch's `aria-describedby` rather than left as
 * decoration, because on these rows it carries the part that disambiguates —
 * "Can be added to tickets" is what tells you which of two similar flags this
 * is. `items-start` so the pill stays level with the first line when the hint
 * wraps, which it does at 320px.
 */
export function SwitchRow({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  const id = React.useId();
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div className="min-w-0">
        <label htmlFor={id} className="block text-sm font-medium">
          {label}
        </label>
        {hint && (
          <p id={hintId} className="mt-0.5 text-xs text-ink-muted">
            {hint}
          </p>
        )}
      </div>
      <Switch
        id={id}
        aria-describedby={hintId}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="mt-0.5"
      />
    </div>
  );
}

/**
 * The same setting, at 48px, with the label inside the hit area — what a tablet
 * on a counter needs and what a 13px checkbox cannot give it.
 *
 * Kept beside `SwitchRow` rather than merged into it: there the row is text and
 * the pill is the control, here the whole bordered row *is* the control. Same
 * picture, different hit area, and collapsing the two would take the 48px
 * target away from the counter. It had been living in `WarehousesPanel.tsx`
 * with two other panels reaching sideways into it for the import.
 */
export function ToggleRow({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex w-full items-center justify-between gap-4 rounded-lg border p-3 text-start disabled:opacity-50"
    >
      <span>
        <span className="block text-sm font-medium">{label}</span>
        {hint && <span className="block text-xs text-ink-muted">{hint}</span>}
      </span>
      <span
        className={
          checked
            ? 'relative h-7 w-12 shrink-0 rounded-full bg-accent-solid transition-colors'
            : 'relative h-7 w-12 shrink-0 rounded-full bg-line-strong transition-colors'
        }
      >
        <span
          className={
            checked
              ? 'absolute top-1 h-5 w-5 rounded-full bg-white transition-all end-1'
              : 'absolute top-1 h-5 w-5 rounded-full bg-white transition-all start-1'
          }
        />
      </span>
    </button>
  );
}
